import React, { useState, useEffect, useCallback } from 'react';
import { SokobanGame, LEVELS } from './gameLogic';
import GameBoard from './components/GameBoard';
import GameControls from './components/GameControls';
import LevelSelector from './components/LevelSelector';
import MobileControls from './components/MobileControls';
import 'bootstrap/dist/css/bootstrap.min.css';
import './App.css';

function App() {
  const [currentLevel, setCurrentLevel] = useState(0);
  const [game, setGame] = useState(null);
  const [gameState, setGameState] = useState(null);
  const [showMobileControls, setShowMobileControls] = useState(false);

  useEffect(() => {
    const newGame = new SokobanGame(LEVELS[currentLevel]);
    setGame(newGame);
    setGameState(newGame.getState());
  }, [currentLevel]);

  const handleMove = useCallback((dx, dy) => {
    if (game && game.movePlayer(dx, dy)) {
      setGameState(game.getState());
    }
  }, [game]);

  const handleReset = useCallback(() => {
    if (game) {
      game.reset();
      setGameState(game.getState());
    }
  }, [game]);

  const handleLevelChange = useCallback((levelIndex) => {
    setCurrentLevel(levelIndex);
  }, []);

  const handleSwipe = useCallback((direction) => {
    switch (direction) {
      case 'up':
        handleMove(0, -1);
        break;
      case 'down':
        handleMove(0, 1);
        break;
      case 'left':
        handleMove(-1, 0);
        break;
      case 'right':
        handleMove(1, 0);
        break;
      default:
        break;
    }
  }, [handleMove]);

  // Keyboard controls
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (gameState?.isWon) return;

      switch (e.key.toLowerCase()) {
        case 'arrowup':
        case 'w':
          handleMove(0, -1);
          break;
        case 'arrowdown':
        case 's':
          handleMove(0, 1);
          break;
        case 'arrowleft':
        case 'a':
          handleMove(-1, 0);
          break;
        case 'arrowright':
        case 'd':
          handleMove(1, 0);
          break;
        case 'r':
          handleReset();
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [handleMove, handleReset, gameState]);

  if (!gameState) {
    return (
      <div className="container-fluid vh-100 d-flex align-items-center justify-content-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="App container-fluid px-0 vh-100 d-flex flex-column">
      {/* Header */}
      <header className="bg-primary text-white py-2 px-3 sticky-top">
        <div className="d-flex justify-content-between align-items-center">
          <h1 className="h5 mb-0">Sokoban</h1>
          <div className="d-flex align-items-center gap-2">
            <span className="badge bg-light text-dark">
              Moves: {gameState.moves}
            </span>
            <span className="badge bg-success">
              Boxes: {gameState.boxesOnTargets}/{gameState.totalTargets}
            </span>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-grow-1 d-flex flex-column" style={{ overflow: 'hidden' }}>
        {/* Level Selector */}
        <div className="px-3 pt-2">
          <LevelSelector 
            levels={LEVELS}
            currentLevel={currentLevel}
            onLevelChange={handleLevelChange}
          />
        </div>

        {/* Game Board */}
        <div className="flex-grow-1 d-flex align-items-center justify-content-center p-2"
             style={{ minHeight: '0' }}>
          <div className="game-container">
            <GameBoard 
              level={gameState.level} 
              onSwipe={handleSwipe}
            />
          </div>
        </div>

        {/* Mobile Controls Toggle */}
        <div className="px-3 pb-2">
          <button
            className="btn btn-outline-primary w-100 btn-sm"
            onClick={() => setShowMobileControls(!showMobileControls)}
          >
            {showMobileControls ? 'Hide Controls' : 'Show Controls'} ⬆️⬇️
          </button>
        </div>
      </main>

      {/* Mobile Controls */}
      {showMobileControls && (
        <MobileControls onMove={handleMove} isWon={gameState.isWon} />
      )}

      {/* Fixed Action Buttons */}
      <footer className="sticky-bottom bg-white border-top p-2">
        <div className="d-flex gap-2">
          <button 
            className="btn btn-warning flex-fill"
            onClick={handleReset}
          >
            🔄 Reset
          </button>
          <button 
            className="btn btn-info flex-fill"
            onClick={() => setShowMobileControls(!showMobileControls)}
          >
            🎮 {showMobileControls ? 'Hide' : 'Controls'}
          </button>
        </div>
      </footer>

      {/* Win Modal */}
      {gameState.isWon && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header bg-success text-white">
                <h5 className="modal-title">🎉 Congratulations!</h5>
              </div>
              <div className="modal-body text-center">
                <p>You completed the level in <strong>{gameState.moves}</strong> moves!</p>
                <div className="display-1">🏆</div>
              </div>
              <div className="modal-footer">
                <button 
                  className="btn btn-primary"
                  onClick={handleReset}
                >
                  Play Again
                </button>
                {currentLevel < LEVELS.length - 1 && (
                  <button 
                    className="btn btn-success"
                    onClick={() => {
                      setCurrentLevel(currentLevel + 1);
                      setShowMobileControls(false);
                    }}
                  >
                    Next Level
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;